// A client.query() you can CANCEL — the missing piece of the manual-query
// composables (owner ruling 2026-08-24: "changing filters whilst loading data
// affected by those filters must cancel the in-flight requests").
//
// `.toPromise()` keeps the operation subscribed until its first settled
// result, so a superseded request could only ever be IGNORED, never stopped:
// the fetch kept running and the backend kept computing (ScopeDeadline-
// Standings runs for tens of seconds on wide scopes — see the 25s spans in
// Sentry). `cancel()` unsubscribes instead, which urql turns into a teardown
// operation, which fetchExchange turns into an AbortController abort — the
// browser drops the connection and the router cancels the downstream work.
//
// The tracing exchange (src/lib/tracing.ts) already closes the operation's
// span on teardown, so cancelled requests report as 'cancelled' rather than
// leaking open spans.
import type {
  AnyVariables,
  Client,
  OperationContext,
  OperationResult,
  TypedDocumentNode,
} from '@urql/vue'

export interface CancellableQuery<TData> {
  /** First settled (non-stale, complete) result — or null when cancel() won. */
  result: Promise<OperationResult<TData> | null>
  /** Tears the operation down (aborting the underlying fetch) and resolves
   *  `result` with null. Safe to call more than once, and after settlement. */
  cancel(): void
}

export function cancellableQuery<TData = unknown, TVariables extends AnyVariables = AnyVariables>(
  client: Client,
  document: TypedDocumentNode<TData, TVariables>,
  variables: TVariables,
  context?: Partial<OperationContext>,
): CancellableQuery<TData> {
  let finished = false
  let resolveResult!: (r: OperationResult<TData> | null) => void
  const result = new Promise<OperationResult<TData> | null>((resolve) => {
    resolveResult = resolve
  })

  const subscription = client.query(document, variables, context).subscribe((r) => {
    // Parity with toPromise(): skip stale/partial frames, settle on the
    // first complete one.
    if (finished || r.stale || r.hasNext) return
    finished = true
    resolveResult(r)
    // A cache hit emits SYNCHRONOUSLY, before `subscription` is assigned —
    // the microtask hop guarantees the handle exists when we drop it.
    queueMicrotask(() => subscription.unsubscribe())
  })

  return {
    result,
    cancel() {
      if (finished) return
      finished = true
      resolveResult(null)
      subscription.unsubscribe()
    },
  }
}
